import { GridColDef } from "@mui/x-data-grid";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import { Box, Chip, IconButton, Radio, Tooltip, Typography } from "@mui/material";
import { ISelectOption } from "src/Shared/Interfaces";
import { ICompanyOffice } from "../../Interfaces";
import {
  contactGridFlagTags,
  imperansAccessRoleLabel,
  notificationSummary,
  siteAccessLabel,
} from "../../Tools/ContactUtils";

const emDash = (value: any) =>
  value === null || value === undefined || `${value}`.trim() === "" ? "—" : `${value}`;

/** One chip treatment for every tag, so the Flags column reads as one column and not a jumble. */
const flagChipSx = {
  height: 22,
  borderRadius: "11px",
  "& .MuiChip-label": { px: 1, fontSize: 12, fontWeight: 500 },
} as const;

/**
 * The contact grid (S4.1/S4.6): the joined name with the job title beneath it and never an empty
 * name cell, "All sites" when no sites are ticked, and every flag readable without opening the
 * contact.
 *
 * Main and RTP are the radios — they set the singleton as well as show it — so the Flags column
 * carries only the flags any number of contacts may hold. Text columns flex so the row fills the
 * grid's width instead of scrolling sideways.
 */
export const contactColumns = (
  updateContact: (contact: any) => void,
  vendorTypes: ISelectOption[],
  sites: ICompanyOffice[] = [],
  makeMain?: (contact: any) => void,
  makeRtp?: (contact: any) => void,
  occasions: { key: string; label: string }[] = [],
  readOnly: boolean = false,
  /** True while a Main/RTP change is still saving, so a second click cannot fire before the row's
   * own checked state has caught up (see the comment on the radios below). */
  savingSingleton: boolean = false,
  /** Contacts could be added but never removed anywhere — no bin, on this grid or in the Edit
   * Contact dialog (bug 5829). Optional so a caller that has not wired removal yet just keeps the
   * Edit-only column it already had. */
  removeContact?: (contact: any) => void,
): GridColDef[] => {
  return [
    {
      field: "fullName",
      headerName: "Name",
      editable: false,
      flex: 1.3,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2">
            {params.row.fullName ||
              [params.row.firstName, params.row.lastName].filter(Boolean).join(" ") ||
              "—"}
          </Typography>
          {params.row.jobTitle && (
            <Typography variant="caption" color="text.secondary" display="block">
              {params.row.jobTitle}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "emailAddress",
      headerName: "Email",
      editable: false,
      flex: 1.3,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => <span>{emDash(params.row.emailAddress)}</span>,
    },
    {
      field: "phoneNumber",
      headerName: "Mobile / Phone",
      editable: false,
      flex: 0.8,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => <span>{emDash(params.row.phoneNumber)}</span>,
    },
    {
      field: "imperansRoleID",
      headerName: "Imperans Role",
      editable: false,
      flex: 1,
      minWidth: 130,
      sortable: false,
      renderCell: (params) => <span>{imperansAccessRoleLabel(params.row.imperansRoleID)}</span>,
    },
    {
      field: "siteIDs",
      headerName: "Site access",
      editable: false,
      flex: 0.9,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={siteAccessLabel(params.row.siteIDs, sites)}>
          <Typography variant="body2" noWrap>
            {siteAccessLabel(params.row.siteIDs, sites)}
          </Typography>
        </Tooltip>
      ),
    },
    // readOnly disables every row's radio, not only the checked one: on a frozen (submitted)
    // onboarding, an unselected row was still clickable and silently moved Main/RTP on a
    // point-in-time record — the RTP is who results go to, so that is not cosmetic (bug 5818).
    //
    // savingSingleton disables both columns, on every row, for as long as a change is still in
    // flight: the row's own `isPrimaryContact`/`isRequestingTestPersonnel` only flips once the grid
    // refetches after the save lands, so between click and refetch every row still reads as
    // unchecked and stayed clickable — a fast double click (or a click on a different row) fired a
    // second, racing change before the first had even landed.
    //
    // onDoubleClick stops the click reaching the row: the grid opens Edit Contact on a row double
    // click, and that dialog carries its own Main/RTP switches, saved through a completely separate
    // path this component never sees — so double-clicking the radio, even once disabled here, could
    // still change Main or RTP through the dialog that popped open underneath it.
    {
      field: "isPrimaryContact",
      headerName: "Main",
      editable: false,
      width: 72,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip title="Make this the main contact">
          <Radio
            size="small"
            checked={Boolean(params.row.isPrimaryContact)}
            disabled={
              !makeMain || readOnly || savingSingleton || Boolean(params.row.isPrimaryContact)
            }
            onChange={() => makeMain && makeMain(params.row)}
            onDoubleClick={(e) => e.stopPropagation()}
          />
        </Tooltip>
      ),
    },
    {
      field: "isRequestingTestPersonnel",
      headerName: "RTP",
      editable: false,
      width: 72,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip title="Make this contact the Report to Person">
          <Radio
            size="small"
            checked={Boolean(params.row.isRequestingTestPersonnel)}
            disabled={
              !makeRtp ||
              readOnly ||
              savingSingleton ||
              Boolean(params.row.isRequestingTestPersonnel)
            }
            onChange={() => makeRtp && makeRtp(params.row)}
            onDoubleClick={(e) => e.stopPropagation()}
          />
        </Tooltip>
      ),
    },
    {
      field: "flags",
      headerName: "Flags",
      editable: false,
      flex: 1.1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        const tags = contactGridFlagTags(params.row);
        if (!tags.length) return <span>—</span>;
        return (
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              flexWrap: "wrap",
              alignItems: "center",
              py: 0.5,
            }}
          >
            {tags.map((tag) => (
              <Chip key={tag} size="small" variant="outlined" label={tag} sx={flagChipSx} />
            ))}
          </Box>
        );
      },
    },
    {
      field: "notifications",
      headerName: "Result notifications",
      editable: false,
      flex: 1.2,
      minWidth: 160,
      sortable: false,
      renderCell: (params) =>
        params.row.hasResultsAccess ? (
          <Tooltip title={notificationSummary(params.row.notifications, occasions, sites)}>
            <Typography variant="caption" noWrap>
              {notificationSummary(params.row.notifications, occasions, sites)}
            </Typography>
          </Tooltip>
        ) : (
          <span>—</span>
        ),
    },
    {
      field: "vendorTypeID",
      headerName: "Contact Type",
      editable: false,
      flex: 0.8,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <span>
          {emDash(
            vendorTypes
              .filter((x) => (params.row.vendorTypeID || []).includes(x.value))
              .map((x) => x.label)
              .join(", "),
          )}
        </span>
      ),
    },
    {
      field: "edit",
      headerName: "",
      editable: false,
      width: removeContact ? 96 : 64,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <>
          <IconButton
            size="small"
            color="primary"
            disabled={readOnly}
            onClick={() => updateContact(params.row)}
          >
            <EditTwoToneIcon fontSize="small" />
          </IconButton>
          {removeContact && (
            <IconButton
              size="small"
              color="error"
              disabled={readOnly}
              onClick={() => removeContact(params.row)}
            >
              <DeleteTwoToneIcon fontSize="small" />
            </IconButton>
          )}
        </>
      ),
    },
  ];
};
